/**
 * Collects keyboard / mouse / touch input into simple intents consumed by the player controller.
 */
export class Input {
  readonly keys = new Set<string>();
  lookDX = 0;
  lookDY = 0;
  wheel = 0;
  /** touch joystick vector (-1..1), x = strafe, y = forward */
  stick = { x: 0, y: 0 };
  touchUp = false;
  touchDown = false;
  pointerLocked = false;
  private pressed = new Set<string>();
  private lookPointer: number | null = null;
  private lastLook = { x: 0, y: 0 };
  enabled = true;

  constructor(private readonly el: HTMLElement) {
    window.addEventListener('keydown', (e) => {
      if (!this.enabled || this.isTyping(e)) return;
      if (!this.keys.has(e.code)) this.pressed.add(e.code);
      this.keys.add(e.code);
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.reset());
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.reset(); });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.el;
    });
    document.addEventListener('mousemove', (e) => {
      if (this.pointerLocked) {
        this.lookDX += e.movementX;
        this.lookDY += e.movementY;
      }
    });
    el.addEventListener('wheel', (e) => {
      this.wheel += Math.sign(e.deltaY);
      e.preventDefault();
    }, { passive: false });
    // drag-to-look for touch and for mouse when not pointer-locked
    el.addEventListener('pointerdown', (e) => {
      if (!this.enabled) return;
      if (e.pointerType === 'mouse' && !this.pointerLocked && e.button === 0 && !matchMedia('(pointer: coarse)').matches) {
        Promise.resolve(el.requestPointerLock?.()).catch(() => undefined);
        return;
      }
      if (this.lookPointer === null) {
        this.lookPointer = e.pointerId;
        this.lastLook = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
    });
    el.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.lookPointer) return;
      const k = e.pointerType === 'touch' ? 1.6 : 1;
      this.lookDX += (e.clientX - this.lastLook.x) * k;
      this.lookDY += (e.clientY - this.lastLook.y) * k;
      this.lastLook = { x: e.clientX, y: e.clientY };
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId === this.lookPointer) this.lookPointer = null;
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', end);
  }

  private isTyping(e: KeyboardEvent) {
    const t = e.target as HTMLElement | null;
    return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');
  }

  /** True once per key press. */
  consumePress(code: string): boolean {
    if (this.pressed.has(code)) {
      this.pressed.delete(code);
      return true;
    }
    return false;
  }

  endFrame() {
    this.lookDX = 0;
    this.lookDY = 0;
    this.wheel = 0;
    this.pressed.clear();
  }

  reset() {
    this.keys.clear();
    this.stick.x = this.stick.y = 0;
    this.touchUp = this.touchDown = false;
    this.lookPointer = null;
  }

  axis(neg: string[], pos: string[]): number {
    let v = 0;
    if (neg.some((k) => this.keys.has(k))) v -= 1;
    if (pos.some((k) => this.keys.has(k))) v += 1;
    return v;
  }
}
